import { useState, useEffect } from 'react';
import { paymentsAPI, claimsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { StatCard, MiniMetric, WelcomeBanner } from '../_shared/DashboardParts';

export default function ManagerDashboard() {
  const { user } = useAuthStore();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [stats, setStats] = useState({ payments: null, claims: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [paymentStats, claimStats] = await Promise.all([
          paymentsAPI.stats(selectedYear),
          claimsAPI.stats(selectedYear),
        ]);
        setStats({ payments: paymentStats, claims: claimStats });
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedYear]);

  const welcomeName = user?.full_name || user?.name || 'Manager';
  const p = stats.payments;
  const c = stats.claims;

  return (
    <div className="space-y-3">
      <WelcomeBanner name={welcomeName} subtitle={`Finance & Administration Overview — ${selectedYear}`}>
        <YearSwitcher year={selectedYear} currentYear={currentYear} onChange={setSelectedYear} />
      </WelcomeBanner>

      {loading && (
        <div className="rounded-2xl border border-slate-200/70 bg-white/90 px-4 py-3 text-sm text-slate-500">
          Loading statistics…
        </div>
      )}

      {!loading && (
        <>
          {/* Key Metrics — 3 cards in a row */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard title="Total Invoiced" value={formatM(p?.total_invoiced)} icon="invoice" color="blue" />
            <StatCard title="Total Paid" value={formatM(p?.total_paid)} icon="paid" color="green" />
            <StatCard title="Outstanding" value={formatM(p?.total_outstanding)} icon="alert" color="amber" />
          </div>

          {/* Attention + Snapshot side by side */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.1fr_0.9fr]">
            <AlertCard
              title="Pending Claims"
              value={c?.pending || 0}
              tone="rose"
              note="Claims awaiting review or further action."
            />
            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 mb-3">Quick Snapshot</p>
              <div className="space-y-2">
                <MiniMetric label="Year" value={String(selectedYear)} />
                <MiniMetric label="Outstanding" value={formatK(p?.total_outstanding)} />
                <MiniMetric label="Pending Claims" value={String(c?.pending || 0)} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatM(value) {
  return `TZS ${(Number(value || 0) / 1_000_000).toFixed(2)}M`;
}
function formatK(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `TZS ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `TZS ${(n / 1_000).toFixed(0)}K`;
  return `TZS ${n.toFixed(0)}`;
}

function YearSwitcher({ year, currentYear, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={() => onChange((y) => y - 1)}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-white transition hover:bg-white/20">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="m14.25 18-6-6 6-6" />
        </svg>
      </button>
      <span className="rounded-xl border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold tracking-widest text-white">
        {year}
      </span>
      <button type="button" onClick={() => onChange((y) => y + 1)} disabled={year >= currentYear}
        className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white/10 text-white transition hover:bg-white/20 disabled:opacity-40">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 6 6 6-6 6" />
        </svg>
      </button>
    </div>
  );
}

function DashboardSection({ eyebrow, title, children }) {
  return (
    <section className="rounded-[26px] border border-slate-200/70 bg-white/88 p-5 shadow-[0_14px_38px_rgba(15,23,42,0.06)]">
      <div className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-orange-600">{eyebrow}</p>
        <h3 className="mt-2 text-xl font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function AlertCard({ title, value, tone, note }) {
  const toneClasses = {
    rose: 'border-rose-200/70 bg-[linear-gradient(145deg,rgba(255,241,242,0.92),rgba(255,255,255,0.98))]',
  };
  return (
    <div className={`rounded-2xl border p-4 shadow-[0_6px_20px_rgba(15,23,42,0.05)] ${toneClasses[tone]}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">{title}</p>
      <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{note}</p>
    </div>
  );
}
